"use client";

import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
  type ToastPosition,
} from "@shared/src/components/ui";
import { useToast } from "@shared/src/hooks/use-toast";
import { Spinner } from "@shared/src/components/ui/common/misc/Spinner";
import { cn } from "@shared/src/utils/cn";

const VIEWPORT_POSITIONS: ToastPosition[] = [
  "top-left",
  "top-center",
  "top-right",
  "bottom-left",
  "bottom-center",
  "bottom-right",
];

export function Toaster() {
  const { toasts } = useToast();

  // Convert the position-based object structure to array for rendering
  const allToasts = Object.entries(toasts)
    .flatMap(([position, positionToasts]) =>
      positionToasts.map(toast => ({
        ...toast,
        position: (toast.position || position) as ToastPosition,
      })),
    )
    .filter(toast => toast.position && Object.keys(toasts).includes(toast.position));

  // Group toasts by position
  const groupedToasts = allToasts.reduce((acc, toast) => {
    const position = toast.position as ToastPosition;
    if (!acc[position]) {
      acc[position] = [];
    }
    acc[position].push(toast);
    return acc;
  }, {} as Record<ToastPosition, typeof allToasts>);

  // Create a separate provider for each direction to handle swipe gestures correctly
  const getSwipeDirection = (position: ToastPosition): "up" | "down" | "left" | "right" => {
    if (position.includes("top")) return "up";
    if (position.includes("bottom")) return "down";
    if (position.includes("left")) return "right";
    if (position.includes("right")) return "left";
    return "right";
  };

  return (
    <>
      {VIEWPORT_POSITIONS.map(position => (
        <ToastProvider key={position} swipeDirection={getSwipeDirection(position)}>
          <ToastViewport position={position}>
            {groupedToasts[position]?.map(toastItem => {
              const { id, title, description, action, loading, dismissible = true, ...rest } = toastItem as any;
              return (
                <Toast
                  key={id}
                  {...(rest as any)}
                  position={position}
                  dismissible={dismissible}
                  className={cn((rest as any).className)}
                >
                  <div className="grid gap-1">
                    {title && <ToastTitle>{title}</ToastTitle>}
                    {description && (
                      <ToastDescription>
                        {loading === true ? (
                          <div className="flex items-center gap-2">
                            <Spinner size={16} className="text-text-primary" />
                            {description}
                          </div>
                        ) : (
                          description
                        )}
                      </ToastDescription>
                    )}
                  </div>
                  {action}
                  {dismissible && <ToastClose />}
                </Toast>
              );
            })}
          </ToastViewport>
        </ToastProvider>
      ))}
    </>
  );
}
